import { ComponentRef, Injectable } from '@angular/core';
import {MmAlertComponent, MmAlertService, MmAlertSize, MmAlertType} from '@metromarkets/components';
import { MmAlertConfig } from '@metromarkets/components/shared/custom-modules/alert/models/mm-alert-config.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { first, map, take } from 'rxjs/operators';
import { NotificationsResponse } from '~app/modules/checkout/models/notifications.model';
import { FeatureToggleEnum } from '~shared/constants';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { GaTrackingEventAction, GaTrackingEventLabel } from '~shared/modules/ga-tracking/constants';
import { NotificationApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationEventService } from '~shared/modules/application-event/services';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private alertRef: ComponentRef<MmAlertComponent>;
  private alertRefsArray: ComponentRef<MmAlertComponent>[] = [];
  private defaultSize = 'large';
  private isValueAddedServicesEnabled: boolean;
  private blacklistedNotifications: string = "";

  constructor(
    private alertService: MmAlertService,
    private translateService: TranslateService,
    private serverLogger: ServerLoggerService,
    // TODO: Remove this code when the feature flag is removed
    private featureFlagService: ActiveFeaturesConfigcatService,
    private applicationEventService: ApplicationEventService
  ) {
    // TODO: Remove this subscription when the feature flag is removed
    this.featureFlagService
      .isFeatureEnabled(FeatureToggleEnum.FF_CART_WEB_APP_BUYER_VALUE_ADDED_SERVICES)
      .pipe(take(1))
      .subscribe((enabled) => {
        this.isValueAddedServicesEnabled = enabled;
      });
    this.featureFlagService
      .getFeatureValue(FeatureToggleEnum.FF_CART_IGNORE_NOTIFICATION_FLAG_SETTING)
      .pipe(take(1))
      .subscribe((blacklistedNotifications) => {
        this.blacklistedNotifications = blacklistedNotifications;
      });
  }

  notify(options: MmAlertConfig, params?: any) {
    const title = options.title ? options.title : '';
    const message = options.message ? options.message : '';
    const size = options.size ? options.size : this.defaultSize;
    this.translateService
      .stream([message, title], params)
      .pipe(first())
      .subscribe(
        (translate) =>
          (this.alertRef = this.alertService.addAlert({
            ...options,
            title: translate[title] || title,
            message: translate[message] || message,
            size,
          }))
      );
    this.alertRefsArray.push(this.alertRef);
    this.publishNotificationEvent(message);
    return this.alertRef;
  }

  showRemoteNotifications<Type extends { getNotifications: () => Observable<NotificationsResponse> }>(service: Type) {
    service
      .getNotifications()
      .pipe(take(1))
      .subscribe(
        ({ notifications }) => {
          if (!notifications || !notifications.length) {
            return;
          }

          notifications.forEach((notification) => {
            if (
              typeof this.blacklistedNotifications === 'string' &&
              this.blacklistedNotifications.length > 0 &&
              this.blacklistedNotifications.includes(notification.key)
            ) {
              return;
            }
            this.mapServerCartNotification(notification.key).subscribe((mappedNotification) => {
              this.notify(mappedNotification);
            })
          });
        },
        (error) => {
          this.serverLogger.error(`NotificationService error response on ${service.getNotifications}`, error);
        }
      );
  }

  closeNotification(alertRef: ComponentRef<MmAlertComponent>) {
    this.alertService.removeAlert(alertRef);
    this.alertRefsArray = this.alertRefsArray.filter((ref) => ref !== alertRef);
  }

  closeAllNotifications() {
    this.alertRefsArray.forEach((alertRef) => this.alertService.removeAlert(alertRef));
    this.alertRefsArray = [];
  }

  private publishNotificationEvent(message) {
    const messageToLabelMap = {
      'CART.NOTIFICATION.PRICE_CHANGE.MESSAGE': GaTrackingEventLabel.OfferPriceHasChanged,
      'CART.OFFER_LINE.ALERT.VAS_PRODUCT_MODIFIED': GaTrackingEventLabel.VASQuantityHasChanged,
      'CART.OFFER_LINE.ALERT.VAS_REMOVED': GaTrackingEventLabel.VASRemoved,
    };

    const label = messageToLabelMap[message];
    if (label) {
      this.applicationEventService.publish(
        new NotificationApplicationEvent(GaTrackingEventAction.CartNotifications, label)
      );
    }
  }

  private mapServerCartNotification(alertMessageKey: string): Observable<MmAlertConfig> {
    // we can keep this logic because it fits existing alerts that can come from server on order review page
    // after migration to react it will be removed
    const alertTitleKey  = alertMessageKey.replace(/\.MESSAGE$/, '.TITLE');

    return this.translateService
      .get([alertTitleKey, alertMessageKey])
      .pipe(
        first(),
        map(({ [alertTitleKey]: title, [alertMessageKey]: message }) => ({
          type: MmAlertType.info,
          title,
          message,
          size: MmAlertSize.large,
        })),
      );
  }
}
